import React, { useState } from 'react';
import { Select, Typography } from 'antd';
import { MAX_STRUCTURE_SCORE } from '@evaltick/shared';
import type { AssessmentScoreTrend } from '@evaltick/shared';
import { TrendChart } from '@/components/Interview/TrendChart';
import type { ChartDatum, SeriesDef } from '@/components/Interview/TrendChart';
import { useBreakpoint } from '@/hooks';
import styles from './ScoreTrendCard.module.scss';

const { Text } = Typography;

const OVERALL_CHART_HEIGHT = 200;

const OVERALL_SERIES: SeriesDef[] = [
  { dataKey: 'overall', label: 'Overall', color: 'var(--color-primary)' },
  { dataKey: 'competency', label: 'Competency', color: 'var(--color-green)' },
  { dataKey: 'structure', label: 'Structure', color: 'var(--color-periwinkle)', maxValue: MAX_STRUCTURE_SCORE },
];

interface ScoreTrendCardProps {
  scoreTrend: AssessmentScoreTrend | null | undefined;
}

export const ScoreTrendCard: React.FC<ScoreTrendCardProps> = ({ scoreTrend }) => {
  const [seriesKey, setSeriesKey] = useState<string>(OVERALL_SERIES[0].dataKey);
  const { isMobile } = useBreakpoint();

  if (!scoreTrend || scoreTrend.overall.length < 2) return null;

  const overallData: ChartDatum[] = scoreTrend.overall.map((p) => ({
    attempt: p.attemptNumber,
    overall: p.totalScore,
    competency: p.competencyScore,
    structure: p.structureScore,
  }));

  const shown = isMobile ? OVERALL_SERIES.filter((s) => s.dataKey === seriesKey) : OVERALL_SERIES;

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <h3 className={styles.cardTitle}>Score Trend</h3>
          <Text type="secondary" className={styles.cardSub}>
            Across your last {scoreTrend.overall.length} sessions of this interview
          </Text>
        </div>
        {isMobile && (
          <Select
            size="small"
            className={styles.seriesSelect}
            value={seriesKey}
            onChange={setSeriesKey}
            aria-label="Trend metric"
            options={OVERALL_SERIES.map((s) => ({ value: s.dataKey, label: s.label }))}
          />
        )}
      </div>

      <div className={styles.chartGrid}>
        {shown.map((series) => (
          <div key={series.dataKey} className={styles.chartCell}>
            <Text className={styles.chartTitle}>
              {series.maxValue ? `${series.label} (out of ${series.maxValue})` : series.label}
            </Text>
            <TrendChart data={overallData} series={[series]} height={OVERALL_CHART_HEIGHT} />
          </div>
        ))}
      </div>
    </div>
  );
};
